import { useState } from 'react'
import { fDateTime } from '@/utils/dayjs'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { useBoolean } from '@/hooks/use-boolean'
import { Card, CardContent } from '@/components/ui/card'
import { Edit, User, Clock, CheckCircle } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'

import { UserVerifyItem, UserVerifyRole } from './types'

enum UserVerifyRoleEnum {
  USER = 'USER',
  ADMIN = 'ADMIN',
  SHOP = 'SHOP',
}

interface UserVerifyTableProps {
  userVerify: UserVerifyItem[]
  loading?: boolean
  onUserVerifyUpdate: (updatedUserVerify: UserVerifyItem) => void
}

export default function UserVerifyTable({
  userVerify,
  loading = false,
  onUserVerifyUpdate,
}: UserVerifyTableProps) {
  const openEditModal = useBoolean(false)
  const [editFormData, setEditFormData] = useState<UserVerifyItem | null>(null)

  const handleOpenEditModal = (userVerifyItem: UserVerifyItem) => {
    setEditFormData({ ...userVerifyItem })
    openEditModal.onTrue()
  }

  const handleUpdateUserVerify = () => {
    if (editFormData && onUserVerifyUpdate) {
      onUserVerifyUpdate(editFormData)
      openEditModal.onFalse()
      setEditFormData(null)
    }
  }

  const getRoleBadge = (role: UserVerifyRole) => {
    const roleConfig = {
      [UserVerifyRoleEnum.USER]: {
        label: 'ผู้ใช้',
        variant: 'default' as const,
      },
      [UserVerifyRoleEnum.ADMIN]: {
        label: 'แอดมิน',
        variant: 'destructive' as const,
      },
      [UserVerifyRoleEnum.SHOP]: {
        label: 'ร้านค้า',
        variant: 'secondary' as const,
      },
    }

    // ตรวจสอบว่า role ที่รับเข้ามามีใน roleConfig หรือไม่ เพื่อป้องกัน error
    const config = roleConfig[role as UserVerifyRoleEnum]
    if (!config) {
      return <Badge variant="outline">ไม่ทราบบทบาท</Badge>
    }
    return <Badge variant={config.variant}>{config.label}</Badge>
  }

  const getVerificationStatus = (isProfileVerified: boolean) => {
    if (isProfileVerified) {
      return (
        <Badge variant="outline" className="bg-green-50 text-green-700">
          <CheckCircle className="h-3 w-3 mr-1" />
          ยืนยันแล้ว
        </Badge>
      )
    } else {
      return (
        <Badge variant="outline" className="bg-yellow-100 text-yellow-700">
          ยังไม่ยืนยัน
        </Badge>
      )
    }
  }

  // const getBannedStatus = (bannedUntil: Date | null) => {
  //   console.log({ bannedUntil })
  //   if (!bannedUntil) return null

  //   const now = new Date()
  //   const isBanned = bannedUntil > now

  //   if (isBanned) {
  //     return <Badge variant="destructive">ถูกแบน</Badge>
  //   } else {
  //     return null
  //   }
  // }

  if (loading) {
    return (
      <Card className="p-0">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ชื่อผู้ใช้</TableHead>
                <TableHead>อีเมล</TableHead>
                <TableHead>รหัสสมาชิก</TableHead>
                <TableHead>บทบาท</TableHead>
                <TableHead>สถานะการยืนยัน</TableHead>
                {/* <TableHead>รหัสอ้างอิง</TableHead> */}
                {/* <TableHead>สถานะแบน</TableHead> */}
                <TableHead>วันที่สร้าง</TableHead>
                <TableHead className="text-center">จัดการ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {Array.from({ length: 10 }).map((_, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <div className="h-4 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-20 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-24 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-6 w-16 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-4 bg-gray-200 rounded animate-pulse" />
                  </TableCell>
                  <TableCell>
                    <div className="h-8 w-16 bg-gray-200 rounded animate-pulse mx-auto" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    )
  }

  if (userVerify.length === 0) {
    return (
      <div className="border rounded-lg p-8 text-center">
        <User className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-base text-muted-foreground">ไม่พบข้อมูลผู้ใช้</p>
      </div>
    )
  }

  return (
    <div>
      <Card className="p-0">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ชื่อผู้ใช้</TableHead>
                <TableHead>อีเมล</TableHead>
                <TableHead>รหัสสมาชิก</TableHead>
                <TableHead>บทบาท</TableHead>
                <TableHead>สถานะการยืนยัน</TableHead>
                {/* <TableHead>รหัสอ้างอิง</TableHead> */}
                {/* <TableHead>สถานะแบน</TableHead> */}
                <TableHead>วันที่สร้าง</TableHead>
                <TableHead className="text-center">จัดการ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {userVerify.map((item) => {
                const {
                  firstName,
                  lastName,
                  displayName,
                  email,
                  role,
                  isProfileVerified,
                  needChangePassword,
                  memberCode,
                  // refCode,
                  // bannedUntil,
                  createdAt,
                  imageUrl,
                } = item

                const name =
                  firstName && lastName
                    ? `${firstName} ${lastName}`
                    : displayName || '-'

                return (
                  <TableRow key={item.uid}>
                    {/* UID */}
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Avatar className="size-8 rounded-lg">
                          <AvatarImage
                            src={imageUrl}
                            alt={name}
                            className="object-cover"
                          />
                          {name && (
                            <AvatarFallback className="size-8 rounded-lg">
                              {name.slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          )}
                        </Avatar>
                        <span className="font-mono text-xs">{name}</span>
                      </div>
                    </TableCell>

                    <TableCell>
                      <span className="font-mono text-xs">{email || '-'}</span>
                    </TableCell>

                    {/* Member Code */}
                    <TableCell>
                      <span className="font-mono text-xs">{memberCode}</span>
                    </TableCell>

                    {/* Role */}
                    <TableCell>{getRoleBadge(role)}</TableCell>

                    {/* Verification Status */}
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        {getVerificationStatus(isProfileVerified)}
                        {needChangePassword && (
                          <Badge variant="outline" className="text-xs">
                            <Clock className="h-3 w-3 mr-1" />
                            ต้องเปลี่ยนรหัสผ่าน
                          </Badge>
                        )}
                      </div>
                    </TableCell>

                    {/* Ref Code */}
                    {/* <TableCell>
                      <span className="font-mono text-xs">
                        {refCode || '-'}
                      </span>
                    </TableCell> */}

                    {/* Banned Status */}
                    {/* <TableCell>
                      {getBannedStatus(bannedUntil) || (
                        <Badge
                          variant="outline"
                          className="bg-green-50 text-green-700"
                        >
                          <CheckCircle className="h-3 w-3 mr-1" />
                          ปกติ
                        </Badge>
                      )}
                    </TableCell> */}

                    {/* Created Date */}
                    <TableCell>
                      <span className="font-mono text-xs">
                        {fDateTime(createdAt)}
                      </span>
                    </TableCell>

                    {/* Actions */}
                    <TableCell className="text-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenEditModal(item)}
                      >
                        <Edit className="mr-1 h-3 w-3" />
                        แก้ไข
                      </Button>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Edit Modal */}
      <Dialog open={openEditModal.value} onOpenChange={openEditModal.onFalse}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>แก้ไขข้อมูลผู้ใช้</DialogTitle>
          </DialogHeader>

          {editFormData && (
            <div className="space-y-4 py-4">
              {/* UID (Read-only) */}
              <div className="space-y-2">
                <label className="text-sm font-medium">ชื่อผู้ใช้</label>
                <Input
                  value={(() => {
                    const { firstName, lastName, displayName } = editFormData

                    const name =
                      firstName && lastName
                        ? `${firstName} ${lastName}`
                        : displayName || '-'
                    return name
                  })()}
                  disabled
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">UID</label>
                <Input value={editFormData.uid} disabled />
              </div>

              {/* Verification Flags */}
              <div className="grid grid-cols-1 gap-4 pt-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium">ยืนยันโปรไฟล์</label>
                  <Switch
                    checked={editFormData.isProfileVerified}
                    onCheckedChange={(checked) =>
                      setEditFormData({
                        ...editFormData,
                        isProfileVerified: checked,
                      })
                    }
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={openEditModal.onFalse}>
              ยกเลิก
            </Button>
            <Button onClick={handleUpdateUserVerify}>
              บันทึกการเปลี่ยนแปลง
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
